export const CLASSIFICATION_OPTIONS = ["Primary", "Secondary", "Do Not Check"] as const

export interface SearchTerm {
  id: string
  term: string
  isRegex: boolean
  comment?: string
}

export interface SummarizerLabel {
  label: string
  description: string
  documentClasses: Record<string, string>
  documentSubClasses: Record<string, Record<string, string>>
  rawSearchTerms: SearchTerm[]
}

export type SummarizerConfig = Record<string, SummarizerLabel>

export const EXPORT_FILE_NAME = "summarizer_config.json"

export function getTermError(searchTerm: Pick<SearchTerm, "term" | "isRegex">): string | null {
  if (searchTerm.term.trim() === "") return "Search term cannot be empty"
  if (!searchTerm.isRegex) return null

  try {
    new RegExp(searchTerm.term)
    return null
  } catch (error) {
    const reason = error instanceof Error ? error.message : "does not compile"
    return `Invalid regular expression: ${reason}`
  }
}

export function isTermValid(searchTerm: SearchTerm) {
  return getTermError(searchTerm) == null
}

export function getInvalidTermCounts(config: SummarizerConfig): Record<string, number> {
  const counts: Record<string, number> = {}

  for (const [name, label] of Object.entries(config)) {
    const count = label.rawSearchTerms.filter((t) => !isTermValid(t)).length
    if (count > 0) counts[name] = count
  }

  return counts
}

export function validateLabelName(rawName: string, existingNames: string[], ignoreName?: string): string | null {
  const name = rawName.trim()

  if (name === "") return "Label name cannot be empty"
  if (name === "__proto__") return "This label name is not allowed"
  if (existingNames.some((existing) => existing === name && existing !== ignoreName))
    return "A label with this name already exists"

  return null
}

export function createEmptyLabel(name: string, classificationNames: string[] = []): SummarizerLabel {
  return {
    label: name,
    description: "",
    documentClasses: {},
    documentSubClasses: Object.fromEntries(classificationNames.map((classification) => [classification, {}])),
    rawSearchTerms: [],
  }
}

export function ensureDocumentSubClassGroups(config: SummarizerConfig, classificationNames: string[]) {
  const classificationNameSet = new Set(classificationNames)
  let hasChanges = false
  const normalized: SummarizerConfig = {}

  for (const [name, label] of Object.entries(config)) {
    const existing = label.documentSubClasses
    if (classificationNames.every((classification) => Object.hasOwn(existing, classification))) {
      normalized[name] = label
      continue
    }

    hasChanges = true
    const documentSubClasses: Record<string, Record<string, string>> = {}
    for (const classification of classificationNames) {
      documentSubClasses[classification] = Object.hasOwn(existing, classification) ? existing[classification] : {}
    }
    for (const [classification, subClasses] of Object.entries(existing)) {
      if (!classificationNameSet.has(classification)) documentSubClasses[classification] = subClasses
    }

    normalized[name] = { ...label, documentSubClasses }
  }

  return hasChanges ? normalized : config
}

export function getDuplicateName(sourceName: string, existingNames: string[]) {
  let count = existingNames.filter((n) => n.startsWith(`${sourceName}_`)).length + 1
  while (existingNames.includes(`${sourceName}_${count}`)) count++

  return `${sourceName}_${count}`
}

export function duplicateLabel(source: SummarizerLabel, newName: string): SummarizerLabel {
  return {
    label: newName,
    description: source.description,
    documentClasses: { ...source.documentClasses },
    documentSubClasses: Object.fromEntries(
      Object.entries(source.documentSubClasses).map(([group, subs]) => [group, { ...subs }]),
    ),
    rawSearchTerms: [],
  }
}

export class ConfigImportError extends Error {}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function normalizeStringMap(raw: unknown): Record<string, string> {
  if (!isPlainObject(raw)) return {}

  const result: Record<string, string> = {}
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === "string") result[key] = value
  }
  return result
}

function normalizeSubClasses(raw: unknown): Record<string, Record<string, string>> {
  if (!isPlainObject(raw)) return {}

  const result: Record<string, Record<string, string>> = {}
  for (const [group, subs] of Object.entries(raw)) {
    result[group] = normalizeStringMap(subs)
  }
  return result
}

function normalizeTerm(raw: unknown): SearchTerm {
  if (typeof raw === "string") return { id: crypto.randomUUID(), term: raw, isRegex: false }

  if (isPlainObject(raw)) {
    const result: SearchTerm = {
      id: crypto.randomUUID(),
      term: typeof raw.term === "string" ? raw.term : "",
      isRegex: raw.isRegex === true,
    }
    if (typeof raw.comment === "string") result.comment = raw.comment
    return result
  }

  return { id: crypto.randomUUID(), term: "", isRegex: false }
}

export function normalizeConfig(raw: unknown): SummarizerConfig {
  if (!isPlainObject(raw)) {
    throw new ConfigImportError("The file must contain a JSON object keyed by label name")
  }

  const config: SummarizerConfig = {}

  for (const [key, value] of Object.entries(raw)) {
    if (key.trim() === "" || key === "__proto__") throw new ConfigImportError(`"${key}" is not a valid label name`)
    if (!isPlainObject(value)) throw new ConfigImportError(`Label "${key}" is not a valid configuration object`)

    const terms = value.rawSearchTerms ?? []
    if (!Array.isArray(terms)) throw new ConfigImportError(`Label "${key}": rawSearchTerms must be a list`)

    config[key] = {
      label: key,
      description: typeof value.description === "string" ? value.description : "",
      documentClasses: normalizeStringMap(value.documentClasses),
      documentSubClasses: normalizeSubClasses(value.documentSubClasses),
      rawSearchTerms: terms.map(normalizeTerm),
    }
  }

  return config
}

export function parseConfigText(text: string): SummarizerConfig {
  let parsed: unknown

  try {
    parsed = JSON.parse(text)
  } catch {
    throw new ConfigImportError("The file is not valid JSON")
  }

  return normalizeConfig(parsed)
}

export function serializeConfig(config: SummarizerConfig) {
  const serializable: Record<
    string,
    Omit<SummarizerLabel, "rawSearchTerms"> & { rawSearchTerms: Array<Omit<SearchTerm, "id">> }
  > = {}

  for (const [name, label] of Object.entries(config)) {
    serializable[name] = {
      ...label,
      rawSearchTerms: label.rawSearchTerms.map(({ id: _id, ...term }) => term),
    }
  }

  return JSON.stringify(serializable, null, 2)
}

export function downloadTextFile(fileName: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "application/json" }))
  const anchor = document.createElement("a")

  anchor.href = url
  anchor.download = fileName
  anchor.click()
  anchor.remove()

  URL.revokeObjectURL(url)
}

export function getStorageKey(client: string, brand: string) {
  return `unity-config/summarizer-configuration/${client}/${brand}`
}

export function loadStoredConfig(storageKey: string): SummarizerConfig {
  try {
    const stored = localStorage.getItem(storageKey)
    return stored == null ? {} : normalizeConfig(JSON.parse(stored))
  } catch {
    return {}
  }
}

export function saveStoredConfig(storageKey: string, config: SummarizerConfig): boolean {
  try {
    localStorage.setItem(storageKey, JSON.stringify(config))
    return true
  } catch (error) {
    console.error("Unable to persist summarizer configuration", error)
    return false
  }
}
